import React, { useEffect } from 'react';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const PrivacidadePage: React.FC = () => {
  useEffect(() => {
    document.title = 'Política de Privacidade | Do Zero ao Mercado Americano';
  }, []);

  return (
    <div className="min-h-screen bg-[#131314] text-white font-sans">
      <main className="w-full px-5 sm:px-8 lg:px-12 xl:px-[160px] py-10 lg:py-16">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={16} /> Voltar para a página inicial
        </a>

        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/50 border border-emerald-800/50 text-emerald-400 text-xs font-bold uppercase tracking-wide mb-5">
          <ShieldCheck size={14} /> Conforme LGPD
        </div>

        <h1 className="text-3xl lg:text-4xl font-heading font-semibold tracking-tight mb-3">
          Política de Privacidade
        </h1>
        <p className="text-slate-400 text-sm mb-10">
          Última atualização: {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
        </p>

        <div className="space-y-8 text-slate-300 text-[15px] leading-relaxed">

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">1. Introdução</h2>
            <p>
              A <strong className="text-white">GVSI CAPITAL LTDA</strong>, inscrita no CNPJ sob o nº 58.660.640/0001-08 ("nós"), valoriza a privacidade de seus visitantes e está comprometida em proteger seus dados pessoais. Esta Política de Privacidade explica como coletamos, usamos, armazenamos e compartilhamos suas informações ao utilizar nossa página, em conformidade com a <strong className="text-white">Lei Geral de Proteção de Dados (Lei nº 13.709/2018 - LGPD)</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">2. Dados que coletamos</h2>
            <p className="mb-3">Ao se inscrever no evento "Do Zero ao Mercado Americano", coletamos:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-300">
              <li><strong className="text-white">E-mail</strong> e <strong className="text-white">número de WhatsApp</strong>, fornecidos voluntariamente no formulário de inscrição.</li>
              <li>Respostas opcionais ao questionário pós-inscrição (perfil, interesses, faixa etária, renda).</li>
              <li>Dados técnicos: endereço IP, tipo de navegador, sistema operacional, páginas acessadas, data e hora da visita.</li>
              <li>Dados de geolocalização aproximada (estado e cidade) derivados do endereço IP.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">3. Base legal e finalidades</h2>
            <p className="mb-3">Seus dados são tratados com base no seu <strong className="text-white">consentimento</strong> (art. 7º, I, LGPD) e no <strong className="text-white">legítimo interesse</strong> (art. 7º, IX) para:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Confirmar sua inscrição e enviar conteúdo, links de acesso e lembretes do evento.</li>
              <li>Personalizar nossa comunicação de acordo com seu perfil.</li>
              <li>Mensurar o desempenho das nossas campanhas de marketing.</li>
              <li>Cumprir obrigações legais e regulatórias.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">4. Compartilhamento de dados com terceiros</h2>
            <p className="mb-3">
              Compartilhamos dados estritamente necessários com parceiros operacionais, sempre sob cláusulas de confidencialidade e proteção de dados:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong className="text-white">Meta Platforms, Inc. (Facebook/Instagram)</strong> - para mensuração de conversões e anúncios, os dados sensíveis (e-mail e telefone) são enviados com <strong className="text-white">hash criptográfico SHA-256</strong>, conforme exigência e termos da Meta. O endereço IP é enviado em texto claro, conforme permitido.</li>
              <li><strong className="text-white">Google LLC</strong> - Analytics, Tag Manager e Ads, para medir tráfego e eficácia de campanhas.</li>
              <li><strong className="text-white">Serviços de automação e CRM</strong> - para gerenciar o envio de conteúdo e comunicações autorizadas.</li>
              <li><strong className="text-white">Provedores de hospedagem</strong> - para manter a página disponível.</li>
            </ul>
            <p className="mt-3">
              Não vendemos seus dados pessoais a terceiros.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">5. Cookies e tecnologias semelhantes</h2>
            <p>
              Utilizamos cookies e pixels (Meta Pixel, Google Analytics) para analisar a navegação e personalizar anúncios. Você pode gerenciar suas preferências pelo banner exibido no primeiro acesso ou pelas configurações do seu navegador.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">6. Seus direitos como titular</h2>
            <p className="mb-3">Nos termos da LGPD (art. 18), você pode, a qualquer momento:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Confirmar a existência de tratamento dos seus dados.</li>
              <li>Acessar, corrigir ou atualizar seus dados.</li>
              <li>Solicitar anonimização, bloqueio ou eliminação de dados desnecessários.</li>
              <li>Revogar o consentimento a qualquer tempo.</li>
              <li>Portar seus dados a outro fornecedor.</li>
              <li>Solicitar a exclusão dos dados tratados com base no consentimento.</li>
            </ul>
            <p className="mt-3">
              Para exercer esses direitos, envie um e-mail para <a href="mailto:contato@gvsicapital.com.br" className="text-emerald-400 hover:underline">contato@gvsicapital.com.br</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">7. Retenção e segurança</h2>
            <p>
              Mantemos seus dados apenas pelo tempo necessário às finalidades descritas ou conforme exigido por lei. Adotamos medidas técnicas e administrativas de segurança (criptografia em trânsito, controle de acesso, hashing de dados sensíveis em integrações) para proteger suas informações contra acessos não autorizados, perda ou destruição.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">8. Encarregado de Dados (DPO)</h2>
            <p>
              Em caso de dúvidas sobre o tratamento de dados, entre em contato com nosso Encarregado pelo e-mail <a href="mailto:dpo@gvsicapital.com.br" className="text-emerald-400 hover:underline">dpo@gvsicapital.com.br</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-white mb-3">9. Alterações desta Política</h2>
            <p>
              Podemos atualizar esta Política de tempos em tempos. A versão vigente estará sempre disponível nesta página, identificada pela data de atualização acima.
            </p>
          </section>

          <section className="pt-4 border-t border-slate-800">
            <p className="text-sm text-slate-400">
              <strong className="text-white">GVSI CAPITAL LTDA</strong> · CNPJ 58.660.640/0001-08<br />
              Este evento tem caráter educacional. Resultados no mercado financeiro dependem de dedicação e disciplina individual. Não garantimos retornos.
            </p>
          </section>

        </div>
      </main>

      <footer className="border-t border-slate-800 py-6 px-5 sm:px-8 mt-10">
        <p className="text-xs text-slate-500 text-center max-w-xl mx-auto">
          &copy; {new Date().getFullYear()} Giovanni Paganini · GVSI CAPITAL LTDA - 58.660.640/0001-08
        </p>
      </footer>
    </div>
  );
};

export default PrivacidadePage;
